import { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSortBy, useTable } from "react-table";
import { AccountStateEnum, AuthContext } from "../../contexts/AuthContext";
import { TEST_ACCOUNT_OVERRIDE } from "../../hooks/api/history/useCursorPaginatedHistory";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { Button, ButtonThemeEnum } from "../Buttons/styles";
import EmptyContent from "../PortfolioSettings/Table/EmptyContent";
import { PortfolioTableWrapper } from "../PortfolioSettings/Table/style";
import { Spinner } from "../shared/Spinner";
import { SpinnerContainerWrapper } from "../shared/Spinner/style";
import { DefaultCellForColumn } from "../shared/Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../shared/Table/DefaultHeaderForColumn";
import { LoadMoreRow, LoadMoreSentinel } from "./style";

/**
 * Walks up the DOM looking for the nearest ancestor with a vertical
 * scroll surface (overflow-y: auto / scroll / overlay).
 *
 * Only the Y axis counts — `PortfolioTableWrapper` itself has
 * `overflow-x: scroll` for wide columns, but that surface doesn't help
 * an infinite-scroll observer. We need the ancestor that scrolls rows
 * vertically.
 *
 * We do NOT require the element to be overflowing right now;
 * IntersectionObserver uses the element as its clipping box regardless.
 */
function findScrollParent(node: HTMLElement | null): Element | null {
  let el: HTMLElement | null = node?.parentElement ?? null;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if (/(auto|scroll|overlay)/.test(overflowY)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

interface IHistoryTableShellProps<T extends object> {
  columns: ITableColumn<T>[];
  items: T[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onRowClick?: (row: T) => void;
  emptyText: string;
  /**
   * Pixels to subtract from `100%` to leave room for a sticky header.
   * - Provide a number to fix the wrapper at `calc(100% - height)` (Portfolio).
   * - Pass `undefined` to omit the height style entirely so the wrapper
   *   sizes to its content (trading bottom-bar embed, where an outer
   *   scroll surface owns the vertical scroll).
   */
  height?: number;
}

/**
 * Sticky-header table shell with cursor-paginated infinite scroll.
 *
 * Used by Position / Trade / Order history tables. Owns the empty,
 * loading, connect-wallet, and "Load more" states. Auto-loads the next
 * page when the sentinel row enters the viewport; the explicit button
 * stays visible as an accessibility / no-IntersectionObserver fallback.
 */
function HistoryTableShell<T extends object>({
  columns,
  items,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  onRowClick,
  emptyText,
  height,
}: IHistoryTableShellProps<T>) {
  const { t } = useTranslation("app", {
    keyPrefix: "components.History.HistoryTableShell",
  });
  const { accountApiKeyState } = useContext(AuthContext);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLTableRowElement>(null);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: items,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        },
        autoResetSortBy: false,
      } as any,
      useSortBy
    );

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return undefined;
    const node = sentinelRef.current;
    // Use the nearest ancestor that's designated as a scroll surface
    // (overflow: auto/scroll). Falls back to the viewport when no such
    // ancestor exists (Portfolio routes scroll the document body).
    const root = findScrollParent(wrapperRef.current);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingMore) {
          onLoadMore();
        }
      },
      { root, rootMargin: "200px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore, items.length]);

  // The test override bypasses the wallet-connect requirement so that
  // history tables fetch and render purely off `?account=<hardcoded>`.
  const isConnected =
    !!TEST_ACCOUNT_OVERRIDE || accountApiKeyState === AccountStateEnum.OK;

  // `height === undefined` → trading mode → no height style, wrapper sizes
  // to content so the outer `TradingScrollArea` catches the overflow.
  // `height === 0`         → portfolio with no sticky header subtraction.
  // `height > 0`           → portfolio: shrink by the header height.
  const wrapperStyle =
    height === undefined
      ? undefined
      : { height: height > 0 ? `calc(100% - ${height}px)` : "100%" };

  if (!isConnected) {
    return (
      <PortfolioTableWrapper $stickyHeader style={wrapperStyle}>
        <EmptyContent showConnectWallet emptyText={emptyText} />
      </PortfolioTableWrapper>
    );
  }

  if (isLoading && !items.length) {
    return (
      <PortfolioTableWrapper $stickyHeader style={wrapperStyle}>
        <SpinnerContainerWrapper>
          <Spinner />
        </SpinnerContainerWrapper>
      </PortfolioTableWrapper>
    );
  }

  if (!items.length) {
    return (
      <PortfolioTableWrapper $stickyHeader style={wrapperStyle}>
        <EmptyContent emptyText={emptyText} />
      </PortfolioTableWrapper>
    );
  }

  return (
    <PortfolioTableWrapper ref={wrapperRef} $stickyHeader style={wrapperStyle}>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...trProps } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} {...trProps}>
                {headerGroup.headers.map((column: any) => {
                  const { key, ...thProps } = column.getHeaderProps(column.getSortByToggleProps());
                  return (
                    <th key={key} {...thProps}>
                      {column.render("Header")}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { key, ...rowProps } = row.getRowProps();
            return (
              <tr
                key={key}
                {...rowProps}
                onClick={() => onRowClick?.(row.original as T)}
              >
                {row.cells.map((cell) => {
                  const { key, ...tdProps } = cell.getCellProps();
                  return (
                    <td key={key} {...tdProps}>{cell.render("Cell")}</td>
                  );
                })}
              </tr>
            );
          })}
          {hasMore && (
            <LoadMoreRow ref={sentinelRef}>
              <td colSpan={columns.length}>
                <LoadMoreSentinel>
                  {isLoadingMore ? (
                    <Spinner />
                  ) : (
                    <Button
                      buttonTheme={ButtonThemeEnum.NEUTRAL2}
                      onClick={onLoadMore}
                    >
                      {t("load_more", { defaultValue: "Load more" })}
                    </Button>
                  )}
                </LoadMoreSentinel>
              </td>
            </LoadMoreRow>
          )}
        </tbody>
      </table>
    </PortfolioTableWrapper>
  );
}

export default HistoryTableShell;
